import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js';
import { getConfig } from '../config/env';
import { setAuthToken } from '../data/runtime';
import { FAKE_CLAIMS, profileToClaims, type Claims, type OidcProfileLike } from './claims';

export interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous' | 'error' | 'new-password-required';
  claims: Claims | null;
  accessToken: string | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

export const AuthContext = createContext<AuthState>({
  status: 'loading',
  claims: null,
  accessToken: null,
  error: null,
  signIn: async () => {},
  completeNewPassword: async () => {},
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const cfg = getConfig();
  if (cfg.AUTH_MODE === 'fake') {
    return <FakeBridge>{children}</FakeBridge>;
  }
  return <CognitoBridge>{children}</CognitoBridge>;
}

/* ----------------------- Fake bridge (dev mode) ---------------------- */

function FakeBridge({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<Claims | null>(FAKE_CLAIMS);
  const value = useMemo<AuthState>(
    () => ({
      status: claims ? 'authenticated' : 'anonymous',
      claims,
      accessToken: null,
      error: null,
      signIn: async () => {
        setClaims(FAKE_CLAIMS);
      },
      completeNewPassword: async () => {},
      signOut: () => setClaims(null),
    }),
    [claims],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ----------------- Cognito bridge (inline branded form) -------------- */

function CognitoBridge({ children }: { children: ReactNode }) {
  const cfg = getConfig();
  const pool = useMemo(
    () =>
      new CognitoUserPool({
        UserPoolId: cfg.COGNITO_USER_POOL_ID,
        ClientId: cfg.COGNITO_CLIENT_ID,
      }),
    [cfg.COGNITO_USER_POOL_ID, cfg.COGNITO_CLIENT_ID],
  );

  // Active session derived from the SDK's local storage cache.
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [error, setError] = useState<string | null>(null);

  // Pending challenge state — carried across the password form submission
  // and the "set new password" prompt.
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [pendingAttrs, setPendingAttrs] = useState<Record<string, string>>({});

  // Restore session from local storage on mount.
  useEffect(() => {
    const current = pool.getCurrentUser();
    if (!current) {
      setStatus('anonymous');
      return;
    }
    current.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) {
        setStatus('anonymous');
        return;
      }
      // Reject restored sessions whose access token is missing the
      // `custom:role` claim — those were issued by an older trigger version
      // and the API will 401 them. Forcing re-login here gets a fresh token
      // with full claims instead of looping through 401 redirects.
      try {
        const payload = s.getAccessToken().decodePayload() as { ['custom:role']?: unknown };
        if (typeof payload['custom:role'] !== 'string' || payload['custom:role'].length === 0) {
          current.signOut();
          setStatus('anonymous');
          return;
        }
      } catch {
        current.signOut();
        setStatus('anonymous');
        return;
      }
      setSession(s);
      setStatus('authenticated');
    });
  }, [pool]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      setStatus('loading');
      const user = new CognitoUser({ Username: email, Pool: pool });
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      return new Promise<void>((resolve, reject) => {
        const cb: IAuthenticationCallback = {
          onSuccess: (s) => {
            setSession(s);
            setStatus('authenticated');
            resolve();
          },
          onFailure: (err) => {
            setStatus('error');
            setError(err.message || String(err));
            reject(err);
          },
          newPasswordRequired: (userAttributes) => {
            // Strip read-only attrs that Cognito refuses on completeNewPasswordChallenge.
            const writable = { ...userAttributes };
            delete writable.email_verified;
            delete writable.email;
            setPendingUser(user);
            setPendingAttrs(writable);
            setStatus('new-password-required');
            resolve();
          },
        };
        user.authenticateUser(authDetails, cb);
      });
    },
    [pool],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      if (!pendingUser) {
        throw new Error('No pending challenge to complete');
      }
      setError(null);
      setStatus('loading');
      return new Promise<void>((resolve, reject) => {
        pendingUser.completeNewPasswordChallenge(newPassword, pendingAttrs, {
          onSuccess: (s) => {
            setSession(s);
            setPendingUser(null);
            setPendingAttrs({});
            setStatus('authenticated');
            resolve();
          },
          onFailure: (err) => {
            setStatus('new-password-required');
            setError(err.message || String(err));
            reject(err);
          },
        });
      });
    },
    [pendingUser, pendingAttrs],
  );

  const signOut = useCallback(() => {
    const current = pool.getCurrentUser();
    if (current) current.signOut();
    setSession(null);
    setPendingUser(null);
    setStatus('anonymous');
  }, [pool]);

  const claims = useMemo<Claims | null>(() => {
    if (!session) return null;
    const idTokenPayload = session.getIdToken().decodePayload() as OidcProfileLike;
    return profileToClaims(idTokenPayload);
  }, [session]);

  const accessToken = session?.getAccessToken().getJwtToken() ?? null;

  // Push the token into runtime.ts synchronously on every render so the
  // first apiFetch fired by any query already has the Authorization header.
  // Using useEffect here would introduce a one-render race with TanStack
  // Query, producing spurious 401s on the very first request after sign-in.
  setAuthToken(accessToken);

  const value = useMemo<AuthState>(
    () => ({ status, claims, accessToken, error, signIn, completeNewPassword, signOut }),
    [status, claims, accessToken, error, signIn, completeNewPassword, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
